import React from 'react'
import { Routes, Route, Navigate, Outlet } from 'react-router-dom'
import { useAuthStore } from './store/authStore'
import Layout from './components/Layout'
import LandingPage from './pages/LandingPage'
import NewsPage from './pages/NewsPage'
import AlumniLoginPage from './pages/AlumniLoginPage'
import AdminLoginPage from './pages/AdminLoginPage'
import EmployerLoginPage from './pages/EmployerLoginPage'
import AlumniSignupPage from './pages/AlumniSignupPage'
import AdminSignupPage from './pages/AdminSignupPage'
import EmployerSignupPage from './pages/EmployerSignupPage'
import DashboardPage from './pages/DashboardPage'
import AlumniProfilePage from './pages/AlumniProfilePage'
import SurveyPage from './pages/SurveyPage'
import JobMatchingPage from './pages/JobMatchingPage'
import AdminDashboard from './pages/AdminDashboard'
import PendingApprovalPage from './pages/PendingApprovalPage'
import ForgotPasswordPage from './pages/ForgotPasswordPage'
import AdminForgotPasswordPage from './pages/AdminForgotPasswordPage'
import EmployerForgotPasswordPage from './pages/EmployerForgotPasswordPage'
import ResetPasswordPage from './pages/ResetPasswordPage'
import LoadingSpinner from './components/LoadingSpinner'
import DashboardLayout from './components/DashboardLayout'
import EmployerPendingApprovalPage from './pages/EmployerPendingApprovalPage'
import EmployerDeactivatedPage from './pages/EmployerDeactivatedPage'
import EmployerDashboard from './pages/EmployerDashboard'
import EmployerProfilePage from './pages/EmployerProfilePage'
import EmployerJobManagement from './pages/EmployerJobManagement'
import EmployerFindCandidatesPage from './pages/EmployerFindCandidatesPage'
import EmployerInvitationsPage from './pages/EmployerInvitationsPage'
import AlumniMyApplicationsPage from './pages/AlumniMyApplicationsPage'
import BatchChatPage from './pages/BatchChatPage'
import AlumniProvideFeedbackPage from './pages/AlumniProvideFeedbackPage'
import EmployerFeedbackPage from './pages/EmployerFeedbackPage'
import NotificationsPage from './pages/NotificationsPage'
import TwoFactorPage from './pages/TwoFactorPage'

// ─── Role Guard ───────────────────────────────────────────────────────────────
// Redirects to the user's home page if their role doesn't match the required one.
function RoleGuard({ role }: { role: 'alumni' | 'admin' | 'employer' }) {
  const { user } = useAuthStore()
  if (!user) return <Navigate to="/" replace />
  if (user.role !== role) {
    // Redirect each role to its own home
    if (user.role === 'admin') return <Navigate to="/admin" replace />
    if (user.role === 'employer') return <Navigate to="/employer-dashboard" replace />
    return <Navigate to="/dashboard" replace />
  }
  return <Outlet />
}

function App() {
  const { user, initialized, initialize } = useAuthStore()

  React.useEffect(() => {
    initialize()
  }, [initialize])

  if (!initialized) {
    return <LoadingSpinner />
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Routes>
        {/* Public Routes — always accessible */}
        <Route path="/" element={<LandingPage />} />
        <Route path="/home" element={<LandingPage />} />
        <Route path="/news" element={<NewsPage />} />
        <Route path="/login" element={<AlumniLoginPage />} />
        <Route path="/login/admin" element={<AdminLoginPage />} />
        <Route path="/login/employer" element={<EmployerLoginPage />} />
        <Route path="/register" element={<AlumniSignupPage />} />
        <Route path="/register/admin" element={<AdminSignupPage />} />
        <Route path="/register/employer" element={<EmployerSignupPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/forgot-password/admin" element={<AdminForgotPasswordPage />} />
        <Route path="/forgot-password/employer" element={<EmployerForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/verify-otp" element={<TwoFactorPage />} />
        
        {/* Protected Routes */}
        {user?.role === 'alumni' && (user.profile as { approval_status?: string } | undefined)?.approval_status !== 'approved' ? (
          // Alumni not yet approved: lock to pending page
          <>
            <Route path="/pending-approval" element={<PendingApprovalPage />} />
            <Route path="*" element={<Navigate to="/pending-approval" replace />} />
          </>
        ) : user?.role === 'employer' && (user.profile as { approval_status?: string } | undefined)?.approval_status === 'pending' ? (
          // Employer (industry partner) awaiting department approval
          <>
            <Route path="/employer/pending" element={<EmployerPendingApprovalPage />} />
            <Route path="*" element={<Navigate to="/employer/pending" replace />} />
          </>
        ) : user?.role === 'employer' && (user.profile as { approval_status?: string } | undefined)?.approval_status === 'deactivated' ? (
          // Deactivated employer — sign them out and show suspended page
          <>
            <Route path="/employer/deactivated" element={<EmployerDeactivatedPage />} />
            <Route path="*" element={<Navigate to="/employer/deactivated" replace />} />
          </>
        ) : user ? (
          <>
            {/* Out-of-layout routes */}
            <Route path="/pending-approval" element={<PendingApprovalPage />} />
            <Route path="/employer/pending" element={<EmployerPendingApprovalPage />} />
            <Route path="/employer/deactivated" element={<EmployerDeactivatedPage />} />

            <Route element={<Layout />}>
              <Route element={<DashboardLayout />}>
                {/* Shared authenticated routes — accessible to all roles */}
                <Route path="/notifications" element={<NotificationsPage />} />

                {/* Alumni-only routes */}
                <Route element={<RoleGuard role="alumni" />}>
                  <Route path="/dashboard" element={<DashboardPage />} />
                  <Route path="/profile" element={<AlumniProfilePage />} />
                  <Route path="/survey" element={<SurveyPage />} />
                  <Route path="/jobs" element={<JobMatchingPage />} />
                  <Route path="/my-applications" element={<AlumniMyApplicationsPage />} />
                  <Route path="/batch-chat" element={<BatchChatPage />} />
                  <Route path="/provide-feedback" element={<AlumniProvideFeedbackPage />} />
                </Route>
                {/* Admin-only routes */}
                <Route element={<RoleGuard role="admin" />}>
                  <Route path="/admin/*" element={<AdminDashboard />} />
                </Route>
                {/* Employer-only routes */}
                <Route element={<RoleGuard role="employer" />}>
                  <Route path="/employer-dashboard" element={<EmployerDashboard />} />
                  <Route path="/employer/profile" element={<EmployerProfilePage />} />
                  <Route path="/employer/jobs" element={<EmployerJobManagement />} />
                  <Route path="/employer/candidates" element={<EmployerFindCandidatesPage />} />
                  <Route path="/employer/applications" element={<EmployerJobManagement />} />
                  <Route path="/employer/invitations" element={<EmployerInvitationsPage />} />
                  <Route path="/employer/feedback" element={<EmployerFeedbackPage />} />
                </Route>
              </Route>
            </Route>
          </>
        ) : null}
        
        {/* 404 Route */}
        <Route path="*" element={<Navigate to="/" />} />
      </Routes>
    </div>
  )
}

export default App